import { __ } from "@wordpress/i18n";
import { 
  useBlockProps, 
  InspectorControls,
  RichText,
  MediaUpload,
  MediaUploadCheck
} from "@wordpress/block-editor";
import { 
  PanelBody,
  Button,
  TextControl,
  TextareaControl,
  Card,
  CardBody,
  CardHeader
} from "@wordpress/components";

export default function Edit({ attributes, setAttributes }) {
  const { title, description, videoUrl, weatherItems } = attributes;
  
  const blockProps = useBlockProps({
    className: 'weather-section-block'
  });

  const addWeatherItem = () => {
    const newItems = [...weatherItems, {
      id: Date.now(),
      title: '',
      imageUrl: '',
      imageId: null
    }];
    setAttributes({ weatherItems: newItems });
  };

  const updateWeatherItem = (index, field, value) => {
    const newItems = [...weatherItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setAttributes({ weatherItems: newItems });
  };

  const removeWeatherItem = (index) => {
    const newItems = weatherItems.filter((_, i) => i !== index);
    setAttributes({ weatherItems: newItems });
  };

  return (
    <>
      <InspectorControls>
        <PanelBody title={__('Weather Section Settings', 'puroclean')}>
          <TextControl
            label={__('Section Title', 'puroclean')}
            value={title}
            onChange={(value) => setAttributes({ title: value })}
          />
          
          <TextareaControl
            label={__('Description', 'puroclean')}
            value={description}
            onChange={(value) => setAttributes({ description: value })}
            rows={4}
          />
          
          <TextControl
            label={__('Video URL', 'puroclean')}
            value={videoUrl}
            onChange={(value) => setAttributes({ videoUrl: value })}
            help={__('YouTube or other video URL', 'puroclean')}
          />
        </PanelBody>
      </InspectorControls>

      <div {...blockProps}>
        <div className="bg-gray-50 p-6 md:p-8 rounded-lg border border-gray-200">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            
            {/* Video Content */}
            <div className="space-y-4">
              <RichText
                tagName="h2"
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
                value={title}
                onChange={(value) => setAttributes({ title: value })}
                placeholder={__('Enter section title...', 'puroclean')}
              />
              
              <RichText
                tagName="p"
                className="text-base md:text-lg text-gray-700 mb-6"
                value={description}
                onChange={(value) => setAttributes({ description: value })}
                placeholder={__('Enter description...', 'puroclean')}
              />
              
              {videoUrl && (
                <div className="aspect-video rounded-lg overflow-hidden bg-gray-200">
                  <iframe
                    src={videoUrl}
                    className="w-full h-full"
                    frameBorder="0"
                    allowFullScreen
                    title="Weather Video"
                  />
                </div>
              )}
            </div>
            
            {/* Weather Items */}
            <div className="space-y-4">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Weather Updates</h3>
              
              <div className="space-y-3">
                {weatherItems.map((item, index) => (
                  <div key={item.id || index} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="space-y-3">
                      <TextControl
                        value={item.title}
                        onChange={(value) => updateWeatherItem(index, 'title', value)}
                        placeholder={__('Weather item title...', 'puroclean')}
                        className="text-sm font-medium text-gray-900"
                      />
                      
                      <MediaUploadCheck>
                        <MediaUpload
                          onSelect={(media) => {
                            updateWeatherItem(index, 'imageId', media.id);
                            updateWeatherItem(index, 'imageUrl', media.url);
                          }}
                          allowedTypes={['image']}
                          value={item.imageId}
                          render={({ open }) => (
                            <div className="space-y-2">
                              {item.imageUrl ? (
                                <div>
                                  <img 
                                    src={item.imageUrl} 
                                    className="w-full h-32 object-cover rounded"
                                    alt={item.title || 'Weather image'}
                                  />
                                  <Button 
                                    onClick={() => {
                                      updateWeatherItem(index, 'imageId', null);
                                      updateWeatherItem(index, 'imageUrl', '');
                                    }}
                                    variant="secondary"
                                    size="small"
                                    className="mt-2"
                                  >
                                    {__('Remove Image', 'puroclean')}
                                  </Button>
                                </div>
                              ) : (
                                <Button 
                                  onClick={open}
                                  variant="secondary"
                                  size="small"
                                  className="w-full"
                                >
                                  {__('Add Image', 'puroclean')}
                                </Button>
                              )}
                            </div>
                          )}
                        />
                      </MediaUploadCheck>
                      
                      <Button
                        onClick={() => removeWeatherItem(index)}
                        variant="link"
                        isDestructive
                        size="small"
                        className="text-red-600 hover:text-red-800"
                      >
                        {__('Remove Item', 'puroclean')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button
                onClick={addWeatherItem}
                variant="primary"
                size="small"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                {__('Add Weather Item', 'puroclean')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}